import React from "react";
import { FaMapMarkedAlt } from "react-icons/fa";
import { FiMapPin } from "react-icons/fi";

import { cn, toAbsolute } from "./groupPlanningUtils";

const FALLBACK = "/img/no-image.jpg";

export default function DojoInfoCard({
  dojo,
  mapUrl,
  showMap,
  onToggleMap,
  onOpenPhoto,
}) {
  if (!dojo) return null;

  return (
    <div
      className="
        mx-auto mt-10 max-w-5xl overflow-hidden
        rounded-[2.5rem]
        border border-slate-200
        bg-white/90 shadow-xl backdrop-blur-xl
        transition duration-300
        hover:border-orange-300 hover:shadow-2xl
        dark:border-white/10 dark:bg-slate-900/80
        dark:hover:border-orange-500/40
      "
    >
      <div className="grid md:grid-cols-[0.85fr_1.15fr]">
        {/* Photo du dojo */}
        <button
          type="button"
          onClick={onOpenPhoto}
          className="
            group relative block min-h-[260px]
            overflow-hidden text-left
            focus:outline-none focus:ring-4 focus:ring-inset
            focus:ring-orange-500/30
            md:min-h-full
          "
          aria-label={`Agrandir la photo du dojo ${dojo.dojo_nom || ""}`}
        >
          <img
            src={toAbsolute(dojo.dojo_photoURL) || FALLBACK}
            alt={dojo.dojo_nom || "Dojo"}
            className="
              h-full w-full object-cover
              transition duration-700
              group-hover:scale-105
            "
            loading="lazy"
            decoding="async"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = FALLBACK;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

          <div
            className="
              absolute inset-0
              bg-gradient-to-br
              from-transparent via-transparent to-orange-500/25
              opacity-0 transition duration-500
              group-hover:opacity-100
            "
          />

          <div className="absolute bottom-5 left-5 right-5">
            <span
              className="
                inline-flex rounded-full
                border border-white/25
                bg-slate-950/30 px-4 py-2
                text-xs font-bold uppercase
                tracking-[0.16em] text-white
                shadow-lg backdrop-blur-xl
                transition
                group-hover:border-orange-300/60
                group-hover:bg-orange-600/80
              "
            >
              Voir la photo
            </span>
          </div>
        </button>

        {/* Informations du dojo */}
        <div className="relative overflow-hidden p-6 sm:p-8">
          <div
            className="
              pointer-events-none absolute -right-16 -top-16
              h-44 w-44 rounded-full
              bg-orange-500/15 blur-3xl
              dark:bg-orange-500/10
            "
          />

          <div
            className="
              pointer-events-none absolute -bottom-20 -left-20
              h-40 w-40 rounded-full
              bg-blue-500/10 blur-3xl
            "
          />

          <div className="relative">
            <div
              className="
                inline-flex items-center gap-2 rounded-full
                border border-orange-200 bg-orange-50
                px-4 py-2
                text-xs font-bold uppercase tracking-[0.18em]
                text-orange-700
                dark:border-orange-500/30
                dark:bg-orange-500/10
                dark:text-orange-300
              "
            >
              <FaMapMarkedAlt className="text-orange-600 dark:text-orange-400" />
              Lieu de pratique
            </div>

            <h3 className="mt-5 font-barlow text-3xl font-extrabold text-slate-950 dark:text-white sm:text-4xl">
              {dojo.dojo_nom}
            </h3>

            {dojo.dojo_discipline && (
              <p className="mt-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                {dojo.dojo_discipline}
              </p>
            )}

            {dojo.dojo_adresse && (
              <div
                className="
                  mt-5 flex items-start gap-3
                  rounded-2xl
                  border border-slate-200
                  bg-slate-50/80 p-4
                  text-sm leading-7 text-slate-700
                  dark:border-white/10
                  dark:bg-white/5
                  dark:text-slate-200
                "
              >
                <span
                  className="
                    mt-0.5 flex h-9 w-9 shrink-0
                    items-center justify-center
                    rounded-xl
                    bg-orange-100 text-orange-600
                    dark:bg-orange-500/10
                    dark:text-orange-400
                  "
                >
                  <FiMapPin />
                </span>

                <span>{dojo.dojo_adresse}</span>
              </div>
            )}

            {mapUrl && (
              <button
                type="button"
                onClick={onToggleMap}
                aria-expanded={showMap}
                className="
                  mt-6 inline-flex items-center justify-center
                  rounded-full
                  border border-orange-300
                  bg-orange-50 px-5 py-3
                  text-sm font-bold text-orange-700
                  shadow-sm
                  transition duration-200
                  hover:-translate-y-0.5
                  hover:border-orange-600
                  hover:bg-orange-600
                  hover:text-white
                  hover:shadow-lg
                  focus:outline-none
                  focus:ring-4 focus:ring-orange-500/20

                  dark:border-orange-500/40
                  dark:bg-orange-500/10
                  dark:text-orange-300
                  dark:hover:border-orange-400
                  dark:hover:bg-orange-500
                  dark:hover:text-white
                "
              >
                {showMap ? "Masquer la carte" : "Afficher la carte"}
              </button>
            )}

            {showMap && mapUrl && (
              <div
                className="
                  mt-6 overflow-hidden rounded-3xl
                  border border-slate-200
                  bg-slate-100 shadow-inner
                  dark:border-white/10
                  dark:bg-slate-950
                "
              >
                <iframe
                  title={`Carte du dojo ${dojo.dojo_nom}`}
                  src={mapUrl}
                  width="100%"
                  height="240"
                  className={cn("block w-full border-0")}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}